/**
 * SD-8.2 (UX-DR14, §C.4): the fixed generic roof the settings preview is computed on.
 *
 * NEVER A CUSTOMER'S ROOF. The preview answers "what does 18 inches DO", and it must answer it
 * identically for every tenant and every viewer — a real roof would make a workspace-level
 * setting look like a statement about one project, and would put customer geometry on a settings
 * screen that has no business holding it. The face is a plain 13.7 × 9.4 m rectangle (about a
 * 45 × 31 ft plane): the least customer-like roof there is, labelled "Reference only" where it
 * renders.
 *
 * THE SAME GEOMETRY THE CANVAS USES, by import, not by imitation. `referenceEnvelope` calls
 * SD-4.3's `setbackEnvelope` — the exact function `RoofCanvas` draws with — which routes through
 * SD-4.2's `offsetPolygonPerEdge`. Position counts come from the same `packPanels` the layout
 * editor uses, with the same `DEFAULT_MODULE` and the SD-4.5 centered grid. A second
 * implementation here would eventually disagree with the canvas, and a preview that disagrees
 * with the thing it previews is worse than no preview.
 *
 * EVERY EDGE IS LABELLED with one of the three configured kinds. An unlabelled edge would take
 * the conservative maximum (by SD-4.3's fallback rule), and then the rake slider would visibly
 * move an edge the admin was told belongs to the ridge. The rectangle reads as a plan-view gable
 * face: ridge along the top, eave along the bottom, a rake up each side.
 *
 * THE RECORDED LIMITATION (§C.4): the zero-fit warning cannot be reached on this roof — even
 * 48 in on every edge leaves 44 positions (pinned in the test). The guard exists for narrow
 * faces on real roofs. That is a fair criticism of sanity-checking a global value on one
 * reference face, and it is carried here as a stated fact rather than hidden.
 */

import { packPanels } from '@/lib/solar/geometry/pack';
import type { Pt } from '@/lib/solar/geometry/types';
import { DEFAULT_MODULE } from '@/lib/solar/panelGeometry';
import {
  envelopeRegions,
  setbackEnvelope,
  type SetbackInches,
} from '@/lib/solar/setbackEnvelope';
import type { RoofModelV1 } from '@/types/solar/roofModel';
import { SHIPPED_DEFAULTS, type DesignSettingsValues } from './designSettingsForm';

export const REFERENCE_FACE_W_M = 13.7;
export const REFERENCE_FACE_H_M = 9.4;

/** Counter-clockwise, y up: bottom edge first, as the provider normalises real faces. */
// The explicit type parameter keeps the literal contextually typed: bare Object.freeze infers
// its own T, widening kind to string and the polygon points to number[], and then fails the
// annotation. tsc -b caught this; the plain vitest run never did.
export const REFERENCE_PLANE: RoofModelV1['planes'][number] = Object.freeze<
  RoofModelV1['planes'][number]
>({
  id: 'reference-face',
  polygon_m: [
    [0, 0],
    [REFERENCE_FACE_W_M, 0],
    [REFERENCE_FACE_W_M, REFERENCE_FACE_H_M],
    [0, REFERENCE_FACE_H_M],
  ],
  pitch_deg: 0,
  azimuth_deg: 180,
  area_m2: REFERENCE_FACE_W_M * REFERENCE_FACE_H_M,
  edges: [
    { from_index: 0, to_index: 1, kind: 'eave' },
    { from_index: 1, to_index: 2, kind: 'rake' },
    { from_index: 2, to_index: 3, kind: 'ridge' },
    { from_index: 3, to_index: 0, kind: 'rake' },
  ],
  sun: { sunshine_hours_median: null },
});

export const REFERENCE_ROOF: Pick<RoofModelV1, 'planes'> = Object.freeze({
  planes: [REFERENCE_PLANE],
});

/** The placeable regions for the given setbacks — SD-4.3's envelope, verbatim. */
export function referenceEnvelope(setbacks: SetbackInches): Pt[][] {
  return envelopeRegions(setbackEnvelope(REFERENCE_ROOF, setbacks));
}

/**
 * How many module positions fit inside the envelope. Portrait `DEFAULT_MODULE`, SD-4.5's
 * centered grid — the packer configuration the layout editor starts from.
 */
export function referencePositions(setbacks: SetbackInches): number {
  return packPanels(referenceEnvelope(setbacks), {
    panelW: DEFAULT_MODULE.shortM,
    panelH: DEFAULT_MODULE.longM,
    gridOrigin: 'centered',
  }).length;
}

export interface ReferencePreview {
  count: number;
  shipped: number;
  delta: number;
}

/** The current count with its delta against the shipped default (§C.4). */
export function referencePreview(setbacks: SetbackInches): ReferencePreview {
  const count = referencePositions(setbacks);
  const shipped = referencePositions(setbacksFromValues(SHIPPED_DEFAULTS));
  return { count, shipped, delta: count - shipped };
}

type SetbackFields = Pick<
  DesignSettingsValues,
  'setback_ridge_in' | 'setback_eave_in' | 'setback_rake_in'
>;

/** Form fields → the geometry's shape. Number() because PostgREST NUMERIC arrives as string. */
export function setbacksFromValues(values: SetbackFields): SetbackInches {
  return {
    ridge: Number(values.setback_ridge_in),
    eave: Number(values.setback_eave_in),
    rake: Number(values.setback_rake_in),
  };
}
